import { FC, MouseEvent, MouseEventHandler, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MoreVert, DeleteOutlineOutlined } from '@mui/icons-material';
import { Box, Button, Menu, MenuItem } from '@mui/material';
import DOMPurify from 'dompurify';
import moment from 'moment';

import { DraftItem } from '../types';
import { errorLogger } from 'utils/config/firebase/logs';

const Item: FC<DraftItem> = ({ draft, onSelectDraft, onDeleteDraft }) => {
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleDelete = (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setAnchorEl(null);
        onDeleteDraft(draft.document_id);
    };

    const handleClose: MouseEventHandler<HTMLDivElement> = (event) => {
        event.stopPropagation();
        setAnchorEl(null);
    };

    const getValue = () => {
        try {
            let value = '';
            if (draft.draft_value) {
                value = draft.draft_value;
            } else if (draft.value) {
                value = draft.value;
            }

            if (!value) {
                return '';
            }

            const clean = DOMPurify.sanitize(value);
            return clean || '';
        } catch (error) {
            if (error instanceof Error) {
                errorLogger.mediumError(error?.message, 'getValue');
            }
            console.log(error);
            return '';
        }
    };

    return (
        <Box className="draft-card" key={draft.document_id} onClick={() => onSelectDraft(draft)}>
            <Box className="preview">
                <Box className="preview-head align-center mb-16">
                    <Box className="date">
                        {moment(draft.updated_at ?? draft.created_at).format('lll')}
                    </Box>
                    <Box className="preview-actions">
                        {draft.draft_value && (
                            <Box className="preview-actions-draft">
                                {t('web_app.right_sidebar.draft_name')}
                            </Box>
                        )}
                        <Button
                            id="draft-actions"
                            aria-controls={open ? 'drafts-menu' : undefined}
                            aria-expanded={open ? 'true' : undefined}
                            aria-haspopup="true"
                            aria-label="Draft settings"
                            className="menu-trigger-btn"
                            onClick={handleClick}
                        >
                            <MoreVert
                                sx={{
                                    fontSize: 24,
                                    color: '#000',
                                }}
                            />
                        </Button>
                        <Menu
                            id="drafts-menu"
                            anchorEl={anchorEl}
                            open={open}
                            MenuListProps={{
                                'aria-labelledby': 'draft-actions',
                                className: 'menu-items',
                            }}
                            sx={{ zIndex: 1201 }}
                            onClick={handleClose}
                            hideBackdrop
                        >
                            <MenuItem
                                className="menu-delete-btn"
                                component="button"
                                onClick={handleDelete}
                            >
                                <DeleteOutlineOutlined />
                                {t('web_app.right_sidebar.draft_delete')}
                            </MenuItem>
                        </Menu>
                    </Box>
                </Box>
                <div dangerouslySetInnerHTML={{ __html: getValue() }}></div>
            </Box>
        </Box>
    );
};

export default Item;
